import React, { useState } from "react";
import { encryptId } from "../utils/encrypt";
import { FaTrash } from "react-icons/fa";

const FormulationTable = ({
  formulations = [],
  pagination = {},
  onPageChange = () => {},
  listClickHandler,
  onDelete = () => {},
}) => {
  const { page, totalPages } = pagination;

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");

  const goToFormulationDetailPage = (id) => {
    listClickHandler(`${encodeURIComponent(encryptId(id))}`);
  };

  const groupedFormulations = formulations
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .reduce((acc, item) => {
      if (!acc[item.colourName]) {
        acc[item.colourName] = [];
      }
      acc[item.colourName].push(item);
      return acc;
    }, {});

  const earliestPerColour = Object.values(groupedFormulations).map((group) =>
    group.reduce((earliest, current) =>
      new Date(current.createdAt) < new Date(earliest.createdAt)
        ? current
        : earliest
    )
  );

  const handleRowClick = (colourName) => {
    setSelectedGroup(groupedFormulations[colourName]);
    setIsModalOpen(true);
  };

  return (
    <div className="mt-6 px-6">
      {/* Table */}
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-gray-200 rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Colour Name
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Colour Code
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Date
              </th>
            
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Cost after loss and labor
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Base Price With Benefit
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {earliestPerColour.map((item) => {
              const {
                _id,
                colourName,
                colourCode,
                date,
                finalPricePerKg,
              } = item;
              return (
                <tr
                  key={_id}
                  onClick={() => handleRowClick(colourName)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-2 font-medium">{colourName}</td>
                  <td className="px-4 py-2 font-medium">{colourCode}</td>
                  <td className="px-4 py-2">{date}</td>
                  <td className="px-4 py-2">
                    ₹{" "}
                    {(
                      item.basePricePerKg +
                      (item.basePricePerKg * item.lossPercent) / 100 +
                      parseFloat(item.labourCost || 0)
                    ).toFixed(2)}
                  </td>
                  <td className="px-4 py-2">₹ {finalPricePerKg}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-4 gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className={`px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          &laquo;
        </button>

        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => onPageChange(i + 1)}
            className={`px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 ${
              page === i + 1 ? "bg-gray-300" : ""
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className={`px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          &raquo;
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Formulations for "{selectedGroup?.[0]?.colourName}"
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 text-xl"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-gray-200 rounded">
              <table className="min-w-full divide-y divide-gray-200 text-center">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-sm font-semibold text-gray-700">
                      Colour Name
                    </th>
                    <th
                      className="px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer"
                      onClick={() =>
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                      }
                    >
                      Date{" "}
                      <span className="ml-1">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    </th>
                    <th className="px-4 py-2 text-sm font-semibold text-gray-700">
                      RMC(Raw Material Cost)/kg
                    </th>
                    <th className="px-4 py-2 text-sm font-semibold text-gray-700">
                      Cost after loss and labor/kg
                    </th>
                    <th className="px-4 py-2 text-sm font-semibold text-gray-700">
                      Selling Price/kg
                    </th>
                    <th className="px-4 py-2 text-sm font-semibold text-gray-700">
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[...selectedGroup]
                    .sort((a, b) => {
                      const dateA = new Date(a.date);
                      const dateB = new Date(b.date);
                      return sortOrder === "asc"
                        ? dateA - dateB
                        : dateB - dateA;
                    })
                    .map((f) => (
                      <tr
                        key={f._id}
                        onClick={() => goToFormulationDetailPage(f._id)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-4 py-2">{f.colourName}</td>
                        <td className="px-4 py-2">{f.date}</td>
                        <td className="px-4 py-2">₹ {f.basePricePerKg}</td>
                        <td>
                          ₹{" "}
                          {(
                            f.basePricePerKg +
                            (f.basePricePerKg * f.lossPercent) / 100 +
                            parseFloat(f.labourCost || 0)
                          ).toFixed(2)}
                        </td>
                        <td className="px-4 py-2">₹ {f.finalPricePerKg}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(f._id);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulationTable;
